import styled from "styled-components";

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const Toolbar = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

export const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 0.9rem;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 1px;
  }
`;

export const Hint = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.textSecondary};
`;

export const Group = styled.section`
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.surfaceAlt};
  overflow: hidden;
`;

export const GroupHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: transparent;
  border: 0;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};

  &:hover {
    background: ${({ theme }) => theme.surface};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: -2px;
  }
`;

export const GroupTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const GroupMeta = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.textSecondary};
  font-weight: 400;
`;

export const BulkButton = styled.button`
  appearance: none;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.textPrimary};
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 0.7rem;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.surface};
  }
`;

export const ResourceBlock = styled.div`
  padding: 8px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: ${({ theme }) => theme.surface};
  border-top: 1px solid ${({ theme }) => theme.border};
`;

export const ResourceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px dashed ${({ theme }) => theme.border};

  &:last-child {
    border-bottom: 0;
  }
`;

export const ResourceName = styled.span`
  font-size: 0.85rem;
  font-weight: 500;
  color: ${({ theme }) => theme.textPrimary};
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  min-width: 0;
`;

/* Identificador tecnico (recurso.nome) como subtitulo discreto, para
   quem precisa casar com o codigo; o rotulo humano fica em destaque. */
export const ResourceCode = styled.code`
  font-size: 0.68rem;
  font-family: var(--font-mono, monospace);
  color: ${({ theme }) => theme.textSecondary};
  font-weight: 400;
`;

export const ActionsRow = styled.div`
  display: inline-flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

export const Counter = styled.span`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.textSecondary};
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ theme }) => theme.surfaceAlt};
`;

export const EmptyState = styled.p`
  margin: 0;
  padding: 18px;
  text-align: center;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.85rem;
`;

/* ---------------------------------------------------------------------------
 * Arvore Modulo > Pagina > Secao > Funcao (catalogo.modulos).
 * Os chips de funcao reusam o ToggleChip acima — so o agrupamento muda.
 * ------------------------------------------------------------------------- */

/* Modulo: bloco fechado com header forte — a "separacao bem definida por
   modulo" pedida. Sem cor decorativa: separacao por borda + fundo do header. */
export const ModuleBlock = styled.section`
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.surface};
  overflow: hidden;
`;

export const ModuleHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: ${({ theme }) => theme.surfaceAlt};
  border: 0;
  padding: 12px 14px;
  cursor: pointer;
  font-size: 0.92rem;
  font-weight: 700;
  color: ${({ theme }) => theme.textPrimary};
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.surface};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: -2px;
  }
`;

export const ModuleTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const Chevron = styled.span`
  font-size: 0.65rem;
  color: ${({ theme }) => theme.textSecondary};
  width: 14px;
  flex-shrink: 0;
  display: inline-block;
`;

/* Contagem concedidas/total — sempre em mono, como toda contagem do ERP. */
export const CountBadge = styled.span`
  font-family: var(--font-mono, monospace);
  font-size: 0.72rem;
  font-weight: 500;
  color: ${({ theme, $ativo }) => ($ativo ? theme.accent : theme.textSecondary)};
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
  flex-shrink: 0;
`;

export const PageBlock = styled.div`
  border-top: 1px solid ${({ theme }) => theme.border};
`;

export const PageHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: transparent;
  border: 0;
  /* Indentacao: pagina eh filha do modulo. */
  padding: 8px 14px 8px 28px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: -2px;
  }
`;

export const PageBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 2px 14px 12px 42px;
`;

export const SectionLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
  color: ${({ theme }) => theme.textSecondary};
`;

/* Itens da secao: lista em colunas (2 em tela larga, 1 no celular).
   minmax(0, 1fr) + texto que quebra linha = zero scroll lateral. */
export const SecaoChips = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
  gap: 2px 16px;
`;

export const SecaoTopo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 24px;
`;

/* ---------------------------------------------------------------------------
 * Item de permissao (linha): caixa + nome + selos + "?".
 * Cor so comunica estado: azul = vem do cargo, vermelho = negada,
 * laranja = exige MFA, accent = liberada aqui.
 * ------------------------------------------------------------------------- */

export const ItemLinha = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  border-radius: 8px;
  padding: 0 4px 0 0;

  &:hover {
    background: var(--surface-hover, var(--surface-2));
  }
`;

export const ItemLabel = styled.label`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 6px;
  min-height: 32px;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  user-select: none;

  > input {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
    pointer-events: none;
  }

  &:focus-within {
    outline: 2px solid var(--accent);
    outline-offset: -2px;
    border-radius: 8px;
  }
`;

const CORES_CAIXA = {
  checked: { borda: "var(--accent)", fundo: "var(--accent)", texto: "#fff" },
  inherited: { borda: "var(--info-border)", fundo: "var(--info-bg)", texto: "var(--info-text)" },
  revoked: { borda: "var(--danger-border)", fundo: "var(--danger-bg)", texto: "var(--danger-text)" },
  off: { borda: "var(--border-strong, var(--border))", fundo: "var(--surface)", texto: "transparent" },
};

export const ItemCaixa = styled.span`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1.5px solid ${({ $state }) => CORES_CAIXA[$state].borda};
  background: ${({ $state }) => CORES_CAIXA[$state].fundo};
  color: ${({ $state }) => CORES_CAIXA[$state].texto};
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const ItemTexto = styled.span`
  min-width: 0;
  font-size: 0.82rem;
  line-height: 1.3;
  color: ${({ $state }) =>
    $state === "off" ? "var(--ink-2)" : "var(--ink)"};
  text-decoration: ${({ $state }) => ($state === "revoked" ? "line-through" : "none")};
  overflow-wrap: anywhere;
`;

const CORES_SELO = {
  info: ["var(--info-bg)", "var(--info-border)", "var(--info-text)"],
  danger: ["var(--danger-bg)", "var(--danger-border)", "var(--danger-text)"],
  warning: ["var(--warning-bg)", "var(--warning-border)", "var(--warning-text)"],
};

export const ItemSelo = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.65rem;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 999px;
  background: ${({ $tom }) => CORES_SELO[$tom][0]};
  border: 1px solid ${({ $tom }) => CORES_SELO[$tom][1]};
  color: ${({ $tom }) => CORES_SELO[$tom][2]};
  white-space: nowrap;
`;

/* Legenda dos estados, acima da arvore. */
export const Legenda = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  font-size: 0.72rem;
  color: var(--ink-2);

  > span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`;

export const FiltroConcedidas = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: var(--ink-2);
  cursor: pointer;
  white-space: nowrap;
`;

/* Conteudo do balao de ajuda (rodape com "onde fica" e o codigo tecnico). */
export const AjudaSituacao = styled.p`
  && {
    margin: 0;
    font-size: 12px;
    color: var(--ink-2);
  }
`;

export const AjudaMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: 8px;
  border-top: 1px solid var(--border-subtle, var(--border));
  font-size: 11px;
  color: var(--ink-3);

  code {
    font-family: var(--font-mono, monospace);
    font-size: 11px;
  }
`;
